import React, { useEffect, useRef, useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import { MapPin, Radio, BatteryMedium, Thermometer, Wifi, Package } from 'lucide-react';

const BOUNDS = { minX: 10, maxX: 88, minY: 14, maxY: 84 };
// zones in % space [x1,y1,x2,y2]
const ZONES = [
  { i: 0, x1: 8, y1: 16, x2: 50, y2: 52 },
  { i: 1, x1: 52, y1: 46, x2: 92, y2: 86 },
];
// Scripted parcel journey waypoints (Receiving -> Storage -> Shipping)
const STATIONS = [
  { x: 15, y: 22 },
  { x: 30, y: 42 },
  { x: 74, y: 70 },
];
const JOURNEY_TONES = ['slate', 'teal', 'amber'];

function zoneOf(x, y) {
  for (const z of ZONES) {
    if (x >= z.x1 && x <= z.x2 && y >= z.y1 && y <= z.y2) return z.i;
  }
  return -1;
}

export default function LiveMap({ onEvent }) {
  const c = useContent();
  const Icons = c.liveAssetIcons;
  const names = c.liveMap.assets;
  const lm = c.liveMap;
  const jrn = lm.journey;
  const lastIdx = names.length - 1; // parcel = scripted journey actor

  const assetsRef = useRef(names.map((_, i) => {
    const journey = i === lastIdx;
    return {
      x: journey ? STATIONS[0].x : ([22, 68, 44, 78][i] ?? 20 + i * 12),
      y: journey ? STATIONS[0].y : ([30, 22, 58, 66][i] ?? 30),
      vx: (Math.random() - 0.5) * 1.6,
      vy: (Math.random() - 0.5) * 1.6,
      moving: journey ? true : i % 2 === 0,
      battery: 60 + Math.floor(Math.random() * 39),
      signal: -(50 + Math.floor(Math.random() * 25)),
      temp: 18 + Math.floor(Math.random() * 8),
      seen: 0,
      zone: -1,
      trail: [],
      journey,
      target: 0,
      paused: 0,
      stageActive: 0,
    };
  }));

  const [, force] = useState(0);
  const [active, setActive] = useState(0);
  const hoverRef = useRef(null);
  const emit = useRef(onEvent);
  emit.current = onEvent;

  useEffect(() => {
    const tpl = (s, n, z) => s.replace('{n}', n).replace('{z}', lm.zones[z] || '');
    const step = setInterval(() => {
      const arr = assetsRef.current;
      arr.forEach((a, i) => {
        if (a.journey) {
          const st = STATIONS[a.target];
          const dx = st.x - a.x, dy = st.y - a.y;
          const dist = Math.hypot(dx, dy);
          if (a.paused > 0) {
            a.paused -= 1;
            if (a.paused === 0) {
              if (a.target === STATIONS.length - 1) {
                if (emit.current) emit.current({ type: 'shipped', text: jrn.shipped.replace('{n}', names[i]), tone: 'teal' });
                a.target = 0;
                a.x = STATIONS[0].x; a.y = STATIONS[0].y;
                a.trail = [];
              } else {
                a.target += 1;
              }
            }
          } else if (dist < 1.6) {
            a.x = st.x; a.y = st.y;
            a.paused = 18;
            if (emit.current) emit.current({ type: 'arrived', text: jrn.ev[a.target].replace('{n}', names[i]), tone: JOURNEY_TONES[a.target] });
          } else {
            const sp = 1.0;
            a.x += (dx / dist) * sp; a.y += (dy / dist) * sp;
            a.trail = [...a.trail.slice(-12), { x: a.x, y: a.y }];
          }
          a.stageActive = a.target;
          return;
        }
        if (a.moving) {
          if (Math.random() < 0.12) { a.vx += (Math.random() - 0.5) * 0.8; a.vy += (Math.random() - 0.5) * 0.8; }
          a.vx = Math.max(-1.8, Math.min(1.8, a.vx));
          a.vy = Math.max(-1.8, Math.min(1.8, a.vy));
          a.x += a.vx; a.y += a.vy;
          if (a.x < BOUNDS.minX || a.x > BOUNDS.maxX) { a.vx *= -1; a.x = Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, a.x)); }
          if (a.y < BOUNDS.minY || a.y > BOUNDS.maxY) { a.vy *= -1; a.y = Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, a.y)); }
          a.trail = [...a.trail.slice(-9), { x: a.x, y: a.y }];
          a.seen = 0;
          const nz = zoneOf(a.x, a.y);
          if (nz !== a.zone) {
            if (nz >= 0 && emit.current) emit.current({ type: 'entered', text: tpl(lm.ev.entered, names[i], nz), tone: 'teal' });
            else if (nz < 0 && a.zone >= 0 && emit.current) emit.current({ type: 'left', text: tpl(lm.ev.left, names[i], a.zone), tone: 'slate' });
            a.zone = nz;
          }
        } else {
          a.seen = Math.min(99, a.seen + 1);
        }
      });
      force((v) => v + 1);
    }, 90);

    const events = setInterval(() => {
      const arr = assetsRef.current;
      const i = Math.floor(Math.random() * lastIdx); // exclude scripted parcel
      const a = arr[i];
      const r = Math.random();
      if (r < 0.45) {
        a.moving = !a.moving;
        if (emit.current) emit.current({ type: a.moving ? 'started' : 'stopped', text: (a.moving ? lm.ev.started : lm.ev.stopped).replace('{n}', names[i]), tone: a.moving ? 'amber' : 'teal' });
      } else if (r < 0.6) {
        a.battery = Math.max(6, a.battery - (3 + Math.floor(Math.random() * 6)));
        if (a.battery < 25 && emit.current) emit.current({ type: 'low', text: lm.ev.low.replace('{n}', names[i]), tone: 'red' });
      }
      a.signal = -(50 + Math.floor(Math.random() * 30));
      a.temp = 18 + Math.floor(Math.random() * 9);
    }, 3600);

    const cycle = setInterval(() => { if (hoverRef.current == null) setActive((p) => (p + 1) % lastIdx); }, 3400);
    return () => { clearInterval(step); clearInterval(events); clearInterval(cycle); };
  }, [names, lm, jrn, lastIdx]);

  const arr = assetsRef.current;
  const hub = { x: 50, y: 50 };
  const parcel = arr[lastIdx];

  return (
    <div className="relative rounded-[2rem] overflow-hidden border border-white shadow-2xl shadow-teal-900/10 bg-[#0b1220]">
      <style>{`
        @keyframes lm-ripple { 0% { transform: scale(.4); opacity: .7 } 100% { transform: scale(2.6); opacity: 0 } }
        @keyframes lm-hub { 0%,100% { opacity:.5 } 50% { opacity:1 } }
      `}</style>
      <div className="relative h-[360px] md:h-[400px]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(45,212,191,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(45,212,191,0.10) 1px, transparent 1px)',
          backgroundSize: '38px 38px',
        }} />

        {/* zones */}
        {ZONES.map((z) => (
          <div key={z.i} className="absolute rounded-2xl border"
            style={{ left: `${z.x1}%`, top: `${z.y1}%`, width: `${z.x2 - z.x1}%`, height: `${z.y2 - z.y1}%`,
              borderColor: z.i === 0 ? 'rgba(45,212,191,.25)' : 'rgba(34,211,238,.20)', background: z.i === 0 ? 'rgba(45,212,191,.05)' : 'rgba(34,211,238,.05)' }}>
            <span className="absolute top-1.5 left-2.5 text-[10px] font-semibold tracking-wide uppercase text-teal-300/70">{lm.zones[z.i]}</span>
          </div>
        ))}

        {/* radar sweep */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[130%] rounded-full pointer-events-none"
          style={{ background: 'conic-gradient(from 0deg, rgba(45,212,191,0.16), transparent 22%)', animation: 'lm-spin 6s linear infinite' }} />

        {/* mesh lines + trails + journey route */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polyline points={STATIONS.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none" stroke="rgba(148,163,184,.4)" strokeWidth="0.5" strokeDasharray="1.4 1.6" vectorEffect="non-scaling-stroke" />
          {arr.map((a, i) => !a.journey && (
            <line key={'l' + i} x1={hub.x} y1={hub.y} x2={a.x} y2={a.y}
              stroke={a.moving ? 'rgba(45,212,191,.35)' : 'rgba(148,163,184,.18)'} strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
          ))}
          {arr.map((a, i) => a.trail.length > 1 && (
            <polyline key={'t' + i} points={a.trail.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none" stroke={a.journey ? 'rgba(34,211,238,.7)' : 'rgba(45,212,191,.5)'} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" opacity="0.65" />
          ))}
        </svg>

        {/* journey station pads */}
        {STATIONS.map((st, i) => (
          <div key={'st' + i} className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${st.x}%`, top: `${st.y}%` }}>
            <span className="block w-4 h-4 rounded-full border-2 border-cyan-400/50 bg-slate-900/60" />
            <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-cyan-300/80">{jrn.stages[i]}</span>
          </div>
        ))}

        {/* gateway hub */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${hub.x}%`, top: `${hub.y}%` }}>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-teal-400/40" style={{ animation: 'lm-hub 2.4s ease-in-out infinite' }} />
          <span className="relative grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30">
            <Radio className="w-4 h-4" />
          </span>
        </div>

        {/* random assets */}
        {arr.map((a, i) => {
          if (a.journey) return null;
          const Icon = Icons[i % Icons.length];
          const isActive = i === active;
          return (
            <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ left: `${a.x}%`, top: `${a.y}%`, transition: 'left .18s linear, top .18s linear', zIndex: isActive ? 20 : 10 }}
              onMouseEnter={() => { hoverRef.current = i; setActive(i); }}
              onMouseLeave={() => { hoverRef.current = null; }}>
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border"
                style={{ borderColor: a.moving ? 'rgba(251,191,36,.6)' : 'rgba(45,212,191,.6)', animation: 'lm-ripple 2s ease-out infinite' }} />
              <span className={`relative grid place-items-center w-8 h-8 rounded-full shadow-lg transition-colors ${isActive ? 'bg-teal-400 text-slate-900' : a.moving ? 'bg-slate-800 text-amber-300 border border-amber-400/50' : 'bg-slate-800 text-teal-300 border border-teal-400/40'}`}>
                <Icon className="w-4 h-4" />
              </span>
              {isActive && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-44 rounded-xl bg-white/95 backdrop-blur px-3 py-2 text-[11px] shadow-xl ring-1 ring-black/5 z-30">
                  <p className="font-semibold text-slate-900 leading-tight">{names[i]}</p>
                  <p className={`mt-0.5 font-medium ${a.moving ? 'text-amber-600' : 'text-teal-600'}`}>{a.moving ? lm.moving : lm.stable}</p>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-1 text-slate-500">
                    <span className="inline-flex items-center gap-1"><BatteryMedium className="w-3 h-3" />{a.battery}%</span>
                    <span className="inline-flex items-center gap-1"><Wifi className="w-3 h-3" />{a.signal} dBm</span>
                    <span className="inline-flex items-center gap-1"><Thermometer className="w-3 h-3" />{a.temp}°C</span>
                    <span className="inline-flex items-center gap-1"><Radio className="w-3 h-3" />{lm.lastSeen} {a.moving ? 0 : a.seen}{lm.agoUnit}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* scripted parcel */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" data-testid="live-parcel"
          style={{ left: `${parcel.x}%`, top: `${parcel.y}%`, transition: 'left .09s linear, top .09s linear', zIndex: 25 }}>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full border"
            style={{ borderColor: 'rgba(34,211,238,.7)', animation: 'lm-ripple 1.8s ease-out infinite' }} />
          <span className="relative grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-400 text-slate-900 shadow-lg shadow-cyan-500/40 ring-2 ring-white/70">
            <Package className="w-4.5 h-4.5" />
          </span>
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-lg bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] shadow-xl ring-1 ring-black/5">
            <span className="font-semibold text-slate-900">{names[lastIdx]}</span>
            <span className="ml-1 font-medium text-cyan-700">→ {jrn.stages[parcel.stageActive]}</span>
          </div>
        </div>

        {/* header chips */}
        <div className="absolute top-4 left-4 glass rounded-full px-3 py-1.5 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-teal-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
          </span>
          <span className="text-xs font-semibold text-slate-900">{lm.title}</span>
        </div>
        <div className="absolute top-4 right-4 glass rounded-full px-3 py-1.5 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-teal-600" />
          <span className="text-xs font-semibold text-slate-900">{names.length} {lm.online}</span>
        </div>

        {/* journey stepper */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 glass rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="journey-stepper">
          <Package className="w-3.5 h-3.5 text-cyan-600 mr-0.5" />
          {jrn.stages.map((stg, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-slate-400 text-[10px]">›</span>}
              <span className={`text-[11px] font-semibold transition-colors ${i === parcel.stageActive ? 'text-cyan-700' : 'text-slate-400'}`}>{stg}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
